import { useInput, useTranslate } from 'react-admin'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  Typography,
  useMediaQuery
} from '@mui/material'
import React from 'react'
import { Calendar, DateObject, type Value } from 'react-multi-date-picker'
import 'react-multi-date-picker/styles/layouts/mobile.css'
import { concat, find, isEmpty, isNil, map, pull } from 'lodash/fp'
import { eachDayOfInterval, format, subDays } from 'date-fns/fp'
import { useFormContext } from 'react-hook-form'
import log from 'loglevel'

log.setLevel(log.levels.DEBUG)

export const ScheduleInput: React.FC<{
  source: string
  onSet: () => void
  onCancel: () => void
  current: string[] | undefined | null
  setSchedules: (arg: string[] | null | undefined) => void
  maxDate?: Date
}> = ({
  source,
  current,
  onSet,
  onCancel,
  setSchedules,
  maxDate,
  ...props
}) => {
  const translate = useTranslate()
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const today = new Date()
  const start = subDays(1)(today)
  const { setValue, getValues } = useFormContext()
  const { ...data } = getValues()
  const schedules = data[source]
  const [values, setValues] = React.useState<Value[][]>(
    !isNil(schedules)
      ? map((item: string) => [item, item])(schedules)
      : [[today]]
  )
  const [currentDate, setCurrentDate] = React.useState<DateObject>(
    new DateObject(today)
  )
  const dateString = (item: Value | undefined): string =>
    item instanceof Date || item instanceof DateObject
      ? format('yyyy-MM-dd')(item instanceof Date ? item : item.toDate())
      : isNil(item)
      ? 'undefined'
      : item.toString()
  const dateArrayString = (
    items: Value[][] | Value[] | undefined
  ): string[][] =>
    isNil(items)
      ? [['undefined']]
      : map((item: Value[] | Value) =>
          map((subItem: Value) => dateString(subItem))(
            item instanceof Array ? item : [item]
          )
        )(items)
  const jaJp = {
    name: 'ja_jp',
    months: [
      ['1月', '1月'],
      ['2月', '2月'],
      ['3月', '3月'],
      ['4月', '4月'],
      ['5月', '5月'],
      ['6月', '6月'],
      ['7月', '7月'],
      ['8月', '8月'],
      ['9月', '9月'],
      ['10月', '10月'],
      ['11月', '11月'],
      ['12月', '21月']
    ],
    weekDays: [
      ['土曜日', '土'],
      ['日曜日', '日'],
      ['月曜日', '月'],
      ['火曜日', '火'],
      ['水曜日', '水'],
      ['木曜日', '木'],
      ['金曜日', '金']
    ],
    digits: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'],
    meridiems: [
      ['午前', '午前'],
      ['午後', '午後']
    ]
  }
  const { field } = useInput({ source, ...props })
  const setFormValue = (values: DateObject[][]): void => {
    setValues(values)
    const value = map((item: DateObject[]) => dateString(item[0]))(values)
    // setValue(source, value)
    field.onChange(value)
  }
  const onChange = (newValues: DateObject[][]): void => {
    const valuesString = dateArrayString(values)
    if (
      valuesString.toString() ===
      [dateString(today), dateString(today)].toString()
    ) {
      const value = find(
        (item: Value) =>
          (item instanceof Array
            ? dateArrayString(item)
            : dateString(item)
          ).toString() !== valuesString.toString()
      )(newValues)
      if (
        !isNil(value) &&
        value instanceof Array &&
        value[0] instanceof DateObject
      ) {
        const adjust = map((item: Date) => [
          new DateObject(item),
          new DateObject(item)
        ])(eachDayOfInterval({ start: today, end: value[0].toDate() }))
        // setValues(adjust)
        setFormValue(adjust)
        return
      }
    }
    let adjust: DateObject[][] = []
    for (const value of newValues) {
      if (
        value instanceof Array &&
        value.length > 1 &&
        value[0] instanceof DateObject &&
        value[1] instanceof DateObject
      ) {
        if (dateString(value[0]) !== dateString(value[1])) {
          if (dateString(value[0]) === dateString(today)) {
            const flat = map((item: Date) => [
              new DateObject(item),
              new DateObject(item)
            ])(
              eachDayOfInterval({
                start: value[0].toDate(),
                end: value[1].toDate()
              })
            )
            adjust = concat(flat)(adjust)
          } else {
            adjust.push([value[0], value[0]])
            adjust.push([value[1], value[1]])
          }
        } else {
          adjust.push(value)
        }
      } else {
        if (!isNil(value) && !isNil(value[0])) {
          adjust.push([value[0], value[0]])
        }
      }
    }
    setFormValue(adjust)
  }
  const onFocusedDateChange = (
    // @ts-expect-error ts(6133)
    focused: DateObject,
    clicked: DateObject
  ): void => {
    setCurrentDate(clicked)
    if (
      dateString(clicked) === dateString(start) &&
      dateArrayString(values).toString() !== [dateString(start)].toString()
    ) {
      // setValues(values)
      setFormValue(values as DateObject[][])
      return
    }
    const deleted = find(
      (item: DateObject[] | Date) =>
        dateString(clicked) ===
        dateString(item instanceof Array ? item[0] : item)
    )(values)
    if (!isNil(deleted) && !isEmpty(deleted)) {
      setFormValue(pull(deleted)(values as DateObject[][]) as DateObject[][])
    }
  }

  const ClearButton: React.FC = () => {
    const onClick = (): void => {
      setValues([[today]])
      setCurrentDate(new DateObject(today))
      setValue(source, null)
      field.onChange(null)
      setSchedules(null)
    }
    return (
      <Button
        variant="contained"
        onClick={onClick}
        fullWidth={!isDesktop}
        sx={{
          borderRadius: '20px'
        }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {translate('scheduleInput.clear')}
        </Typography>
      </Button>
    )
  }
  const SetButton: React.FC = () => {
    const onClick = (): void => {
      const currentValues = getValues()
      if (!isEmpty(currentValues[source])) {
        const valuesString = dateArrayString(values)
        const value = map((item: string[]) => item[0])(valuesString)
        setValue(source, value)
        setSchedules(value as string[])
      }
      onSet()
    }
    return (
      <Button
        variant="contained"
        onClick={onClick}
        fullWidth={!isDesktop}
        sx={{
          borderRadius: '20px'
        }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {translate('scheduleInput.set')}
        </Typography>
      </Button>
    )
  }
  const CancelButton: React.FC = () => {
    const onClick = (): void => {
      setValue(source, current)
      field.onChange(current)
      onCancel()
    }
    return (
      <Button
        variant="outlined"
        onClick={onClick}
        fullWidth={!isDesktop}
        sx={{
          borderRadius: '20px'
        }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {translate('scheduleInput.cancel')}
        </Typography>
      </Button>
    )
  }
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box width="100%">
        <Grid
          container
          justifyContent="center"
          spacing={2}
          sx={{
            marginTop: '0.1em'
          }}>
          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '1em',
              marginTop: '1em'
            }}>
            <Calendar
              multiple
              range
              value={values}
              currentDate={currentDate}
              minDate={today}
              maxDate={maxDate}
              onChange={onChange}
              onFocusedDateChange={onFocusedDateChange}
              className="rmdp-mobile"
              style={
                isDesktop
                  ? {
                      transform: 'scale(1.5, 1.5)',
                      marginTop: '3em',
                      marginBottom: '3em',
                      fontWeight: 'bold'
                    }
                  : {
                      transform: 'scale(1.2, 1.2)',
                      marginTop: '3em',
                      marginBottom: '1em',
                      fontWeight: 'bold'
                    }
              }
              mapDays={() => {
                const props = {
                  style: {
                    fontSize: '18px',
                    fontWeight: 'bold'
                  }
                }
                return props
              }}
              locale={navigator.language === 'ja' ? jaJp : undefined}
              monthYearSeparator=" "
              // @ts-expect-error ts(6133)
              formatMonth={(month: string, year: string) => {
                return `${year}年`
              }}
              // @ts-expect-error ts(6133)
              formatYear={(year: string, month: stirng) => {
                return month
              }}
              disableYearPicker
              disableMonthPicker
            />
          </Grid>
          <Grid
            container
            justifyContent="center"
            spacing={2}
            sx={{
              marginTop: '1em',
              marginLeft: '0.5em'
            }}>
            <Grid
              item
              xs={isDesktop ? 2 : 12}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: isDesktop ? '2.5em' : '0em'
              }}>
              {isDesktop ? <ClearButton /> : <SetButton />}
            </Grid>
            <Grid
              item
              xs={isDesktop ? 2 : 12}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: isDesktop ? '2.5em' : '0em'
              }}>
              <CancelButton />
            </Grid>
            <Grid
              item
              xs={isDesktop ? 2 : 12}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: isDesktop ? '2.5em' : '0em'
              }}>
              {isDesktop ? <SetButton /> : <ClearButton />}
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}
export const ScheduleButton: React.FC<{
  source: string
  label: string
  setSchedules: (arg: string[] | null | undefined) => void
}> = ({ source, label, setSchedules }) => {
  const translate = useTranslate()
  const [open, setOpen] = React.useState<boolean>(false)
  const { getValues } = useFormContext()
  const current = getValues()
  const onClick = (): void => {
    setOpen(true)
  }
  const handleClose = (): void => {
    setOpen(false)
  }
  return (
    <>
      <Button
        variant="contained"
        size="small"
        onClick={onClick}
        sx={{
          borderRadius: '20px'
          // backgroundColor: '#4472c4'
        }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {translate(label)}
        </Typography>
      </Button>
      <Dialog onClose={handleClose} open={open} maxWidth="md" fullWidth>
        <DialogContent>
          <ScheduleInput
            source={source}
            current={current[source]}
            onSet={handleClose}
            onCancel={handleClose}
            setSchedules={setSchedules}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
